import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  BarChart3, Scale, TrendingUp, TrendingDown, DollarSign, Eye, MousePointer,
  Target, Loader2, Search, Plus, X, CheckCircle2, AlertTriangle, Trophy
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line } from 'recharts';
import api, { campaignAnalyticsAPI } from '../api';

const CompareCampaignsPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [showSelectDialog, setShowSelectDialog] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const res = await api.get('/marketing/campaigns', { params: { limit: 100 } });
      setCampaigns(res.data || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const toggleCampaign = (campaign) => {
    setSelectedCampaigns(prev => {
      const exists = prev.find(c => c.id === campaign.id);
      if (exists) {
        return prev.filter(c => c.id !== campaign.id);
      }
      if (prev.length >= 5) {
        toast.error('Maximum 5 campaigns for comparison');
        return prev;
      }
      return [...prev, campaign];
    });
  };

  const removeCampaign = (campaignId) => {
    setSelectedCampaigns(prev => prev.filter(c => c.id !== campaignId));
    setComparisonData(null);
  };

  const handleCompare = async () => {
    if (selectedCampaigns.length < 2) {
      toast.error('Select at least 2 campaigns to compare');
      return;
    }

    try {
      setComparing(true);
      const res = await campaignAnalyticsAPI.analyzeCampaigns({
        campaign_ids: selectedCampaigns.map(c => c.id),
        include_comparison: true
      });
      setComparisonData(res.data);
      toast.success('Comparison generated!');
    } catch (error) {
      toast.error('Failed to compare campaigns');
    } finally {
      setComparing(false);
    }
  };

  // Prepare chart data
  const getBarChartData = () => {
    if (!comparisonData?.analyses) return [];
    return comparisonData.analyses.map(a => ({
      name: a.campaign_name?.substring(0, 20) || 'Unknown',
      Impressions: a.metrics?.impressions || 0,
      Clicks: a.metrics?.clicks || 0,
      Conversions: a.metrics?.conversions || 0,
    }));
  };

  const getROIChartData = () => {
    if (!comparisonData?.analyses) return [];
    return comparisonData.analyses.map(a => ({
      name: a.campaign_name?.substring(0, 20) || 'Unknown',
      ROI: a.metrics?.roi || 0,
      CTR: a.metrics?.ctr || 0,
      ConvRate: a.metrics?.conversion_rate || 0,
    }));
  };

  const getRadarData = () => {
    if (!comparisonData?.analyses) return [];
    return [
      {
        metric: 'ROI',
        ...Object.fromEntries(comparisonData.analyses.map(a => [a.campaign_name?.substring(0, 15), Math.min(100, Math.max(0, a.metrics?.roi || 0))]))
      },
      {
        metric: 'CTR',
        ...Object.fromEntries(comparisonData.analyses.map(a => [a.campaign_name?.substring(0, 15), Math.min(100, (a.metrics?.ctr || 0) * 20)]))
      },
      {
        metric: 'Conv Rate',
        ...Object.fromEntries(comparisonData.analyses.map(a => [a.campaign_name?.substring(0, 15), Math.min(100, (a.metrics?.conversion_rate || 0) * 10)]))
      },
      {
        metric: 'Budget Use',
        ...Object.fromEntries(comparisonData.analyses.map(a => [a.campaign_name?.substring(0, 15), Math.min(100, a.metrics?.budget_utilization || 0)]))
      },
      {
        metric: 'Score',
        ...Object.fromEntries(comparisonData.analyses.map(a => [a.campaign_name?.substring(0, 15), a.overall_score || 0]))
      },
    ];
  };

  const getScoreChartData = () => {
    if (!comparisonData?.analyses) return [];
    return comparisonData.analyses.map(a => ({
      name: a.campaign_name?.substring(0, 20) || 'Unknown',
      Score: a.overall_score || 0,
      verdict: a.verdict_label,
    }));
  };

  const getVerdictColor = (verdict) => {
    switch (verdict) {
      case 'highly_successful': return 'bg-green-100 text-green-800';
      case 'successful': return 'bg-blue-100 text-blue-800';
      case 'mixed': return 'bg-amber-100 text-amber-800';
      case 'challenging': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className="space-y-6" data-testid="compare-campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Scale className="h-6 w-6" />
            Compare Campaigns
          </h1>
          <p className="text-muted-foreground">Side-by-side campaign performance analysis</p>
        </div>
        <Button onClick={() => setShowSelectDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Select Campaigns
        </Button>
      </div>

      {/* Selected Campaigns */}
      {selectedCampaigns.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Selected Campaigns ({selectedCampaigns.length}/5)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedCampaigns.map((campaign, idx) => (
                <Badge
                  key={campaign.id}
                  variant="outline"
                  className="px-3 py-1.5 text-sm flex items-center gap-2"
                  style={{ borderLeftColor: COLORS[idx], borderLeftWidth: 4 }}
                >
                  {campaign.name}
                  <button onClick={() => removeCampaign(campaign.id)} className="hover:text-red-500">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleCompare} disabled={comparing || selectedCampaigns.length < 2}>
                {comparing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <BarChart3 className="h-4 w-4 mr-2" />
                )}
                Compare Now
              </Button>
              <Button variant="outline" onClick={() => { setSelectedCampaigns([]); setComparisonData(null); }}>
                Clear All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comparison Results */}
      {comparisonData && (
        <div className="space-y-6">
          {/* Winner Highlight */}
          {comparisonData.comparison && (
            <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <Trophy className="h-5 w-5" />
                  Top Performers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">Best ROI</p>
                    <p className="font-semibold">{comparisonData.comparison.best_roi?.campaign_name}</p>
                    <p className="text-green-600 font-bold">{comparisonData.comparison.best_roi?.metrics?.roi?.toFixed(1)}%</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">Best CTR</p>
                    <p className="font-semibold">{comparisonData.comparison.best_ctr?.campaign_name}</p>
                    <p className="text-blue-600 font-bold">{comparisonData.comparison.best_ctr?.metrics?.ctr?.toFixed(2)}%</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">Best Conversion</p>
                    <p className="font-semibold">{comparisonData.comparison.best_conversion?.campaign_name}</p>
                    <p className="text-purple-600 font-bold">{comparisonData.comparison.best_conversion?.metrics?.conversion_rate?.toFixed(2)}%</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">Most Efficient</p>
                    <p className="font-semibold">{comparisonData.comparison.most_efficient?.campaign_name}</p>
                    <p className="text-amber-600 font-bold">{comparisonData.comparison.most_efficient?.overall_score?.toFixed(0)}/100</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Performance Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Performance Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getBarChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Impressions" fill="#3b82f6" />
                    <Bar dataKey="Clicks" fill="#10b981" />
                    <Bar dataKey="Conversions" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Radar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Multi-Metric Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={getRadarData()}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="metric" />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} />
                    {comparisonData.analyses?.map((a, idx) => (
                      <Radar
                        key={a.campaign_id}
                        name={a.campaign_name?.substring(0, 15)}
                        dataKey={a.campaign_name?.substring(0, 15)}
                        stroke={COLORS[idx]}
                        fill={COLORS[idx]}
                        fillOpacity={0.2}
                      />
                    ))}
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ROI/Rates Line Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">ROI & Conversion Rates</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getROIChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="ROI" fill="#10b981" name="ROI %" />
                    <Bar dataKey="CTR" fill="#3b82f6" name="CTR %" />
                    <Bar dataKey="ConvRate" fill="#8b5cf6" name="Conv Rate %" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Overall Score Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Overall Scores</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getScoreChartData()} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value, name, props) => [
                        `${value}/100 (${props.payload.verdict})`,
                        'Score'
                      ]}
                    />
                    <Bar dataKey="Score" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Analysis Cards */}
          <h3 className="text-lg font-semibold mt-8">Detailed Analysis</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {comparisonData.analyses?.map((analysis, idx) => (
              <Card key={analysis.campaign_id} style={{ borderTopColor: COLORS[idx], borderTopWidth: 4 }}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{analysis.campaign_name}</CardTitle>
                    <Badge className={getVerdictColor(analysis.verdict)}>{analysis.verdict_label}</Badge>
                  </div>
                  <CardDescription>Score: {analysis.overall_score}/100</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Metrics */}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="p-2 bg-slate-50 rounded">
                      <p className="text-xs text-muted-foreground">ROI</p>
                      <p className={`font-bold ${analysis.metrics?.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {analysis.metrics?.roi?.toFixed(1)}%
                      </p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded">
                      <p className="text-xs text-muted-foreground">CTR</p>
                      <p className="font-bold">{analysis.metrics?.ctr?.toFixed(2)}%</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded">
                      <p className="text-xs text-muted-foreground">Conv Rate</p>
                      <p className="font-bold">{analysis.metrics?.conversion_rate?.toFixed(2)}%</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded">
                      <p className="text-xs text-muted-foreground">Spend</p>
                      <p className="font-bold">${analysis.metrics?.spend?.toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Success Factors */}
                  {analysis.success_factors?.length > 0 && (
                    <div className="text-sm">
                      <p className="text-green-700 font-medium flex items-center gap-1 mb-1">
                        <CheckCircle2 className="h-3 w-3" /> Strengths
                      </p>
                      <ul className="list-disc list-inside text-xs text-muted-foreground">
                        {analysis.success_factors.slice(0, 2).map((f, i) => (
                          <li key={i}>{f.factor}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Challenges */}
                  {analysis.challenge_factors?.length > 0 && (
                    <div className="text-sm">
                      <p className="text-amber-700 font-medium flex items-center gap-1 mb-1">
                        <AlertTriangle className="h-3 w-3" /> Challenges
                      </p>
                      <ul className="list-disc list-inside text-xs text-muted-foreground">
                        {analysis.challenge_factors.slice(0, 2).map((f, i) => (
                          <li key={i}>{f.factor}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {selectedCampaigns.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <Scale className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-semibold text-xl mb-2">Compare Campaign Performance</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Select 2-5 campaigns to see side-by-side performance charts, ROI comparisons, and detailed analysis.
            </p>
            <Button size="lg" onClick={() => setShowSelectDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Select Campaigns to Compare
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Select Campaigns Dialog */}
      <Dialog open={showSelectDialog} onOpenChange={setShowSelectDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Select Campaigns to Compare</DialogTitle>
            <DialogDescription>Choose 2-5 campaigns for side-by-side comparison</DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[400px] overflow-y-auto py-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <div className="space-y-2">
                {campaigns.map(campaign => (
                  <div
                    key={campaign.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 ${
                      selectedCampaigns.find(c => c.id === campaign.id) ? 'border-blue-500 bg-blue-50' : ''
                    }`}
                    onClick={() => toggleCampaign(campaign)}
                  >
                    <Checkbox
                      checked={!!selectedCampaigns.find(c => c.id === campaign.id)}
                      onCheckedChange={() => toggleCampaign(campaign)}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{campaign.name}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Badge variant="outline" className="text-xs capitalize">{campaign.status}</Badge>
                        <Badge variant="outline" className="text-xs capitalize">{campaign.campaign_type?.replace('_', ' ')}</Badge>
                        {campaign.budget && <span>${campaign.budget.toLocaleString()}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSelectDialog(false)}>Cancel</Button>
            <Button onClick={() => setShowSelectDialog(false)} disabled={selectedCampaigns.length < 2}>
              Compare {selectedCampaigns.length} Campaigns
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompareCampaignsPage;
